import { createAppKit } from "@reown/appkit/react";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { base, type AppKitNetwork } from "@reown/appkit/networks";

/**
 * Reown AppKit wiring. This is the wallet-connection layer: injected wallets on
 * desktop, WalletConnect (QR on desktop, app-to-app deep links on mobile — e.g.
 * Rabby on iOS) everywhere else. We use the wagmi adapter because it's built on
 * viem (already a dependency) and reuses the app's existing react-query client.
 *
 * Connected accounts, chain switching, and viem wallet clients are consumed
 * through wagmi hooks at the call sites.
 */
const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID;

if (!projectId) {
  // Without a (free) Reown Cloud project id the modal can't open. Create one at
  // https://dashboard.reown.com and set VITE_WALLETCONNECT_PROJECT_ID in .env.
  console.error(
    "[wallet] VITE_WALLETCONNECT_PROJECT_ID is not set — wallet connection is disabled.",
  );
}

const networks: [AppKitNetwork, ...AppKitNetwork[]] = [base];

export const wagmiAdapter = new WagmiAdapter({
  networks,
  projectId: projectId ?? "",
  ssr: false,
});

/**
 * WalletConnect Explorer ids for the wallets we feature in the AppKit modal.
 * Search still covers the full Reown wallet list.
 */
const WALLET_IDS = [
  "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96", // MetaMask
  "18388be9ac2d02726dbac9777c96efaac06d744b2f6d580fccdd4127a6d01fd1", // Rabby
  "1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369", // Rainbow
  "fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa", // Coinbase Wallet
];

createAppKit({
  adapters: [wagmiAdapter],
  networks,
  projectId: projectId ?? "",
  metadata: {
    name: "CADC Looper",
    description: "Leverage your CADC and supply USDC on Base.",
    url: typeof window !== "undefined" ? window.location.origin : "https://cadc.loon.finance",
    icons: ["https://cadc.loon.finance/cadc.svg"],
  },
  themeVariables: {
    "--apkt-accent": "#0e0e10",
    "--apkt-font-family": '"Inter Tight", "Söhne", "Inter", -apple-system, system-ui, sans-serif',
    "--w3m-accent": "#0e0e10",
    "--w3m-font-family": '"Inter Tight", "Söhne", "Inter", -apple-system, system-ui, sans-serif',
  },
  // Prioritize common wallets without restricting Reown's full wallet search.
  featuredWalletIds: WALLET_IDS,
  allWallets: "SHOW",
  enableEIP6963: true,
  enableInjected: true,
  // Keep it lean and wallet-only: no email/social login bloat.
  features: {
    analytics: false,
    email: false,
    socials: false,
  },
});
